import React, { useState } from 'react';
import { claimApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  ShieldAlert, ShieldCheck, AlertTriangle, CheckCircle,
  XCircle, Lightbulb, Activity, Loader2, FileWarning, Sparkles
} from 'lucide-react';

const RISK_CONFIG = {
  low: { color: '#166534', bg: '#166534/10', icon: ShieldCheck, label: 'Low Risk' },
  medium: { color: '#92400E', bg: '#92400E/10', icon: AlertTriangle, label: 'Medium Risk' },
  high: { color: '#DC2626', bg: '#DC2626/10', icon: ShieldAlert, label: 'High Risk' },
  critical: { color: '#7F1D1D', bg: '#7F1D1D/10', icon: XCircle, label: 'Critical Risk' },
};

const SEVERITY_BADGE = {
  high: 'bg-[#991B1B]/10 text-[#991B1B] border-[#991B1B]/20',
  medium: 'bg-[#92400E]/10 text-[#92400E] border-[#92400E]/20',
  low: 'bg-[#166534]/10 text-[#166534] border-[#166534]/20',
};

export default function DenialPredictionDialog({ open, onClose, claim }) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runPrediction = async () => {
    if (!claim) return;
    setLoading(true);
    setError(null);
    try {
      const res = await claimApi.predictDenial({
        claim_id: claim.id,
        diagnosis_codes: claim.diagnosis_codes || [],
        procedure_codes: claim.procedure_codes || [],
        insurance_provider: claim.insurance_provider || '',
        total_amount: claim.total_amount || 0,
        patient_name: claim.patient_name || '',
        service_date: claim.service_date || '',
      });
      setPrediction(res.data.prediction);
    } catch (err) {
      setError('Failed to run denial prediction. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    if (open && !prediction && !loading) {
      runPrediction();
    }
  };

  React.useEffect(() => {
    if (open) {
      setPrediction(null);
      setError(null);
      runPrediction();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const riskConfig = prediction ? (RISK_CONFIG[prediction.risk_level] || RISK_CONFIG.medium) : null;
  const RiskIcon = riskConfig?.icon || Activity;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh]" data-testid="denial-prediction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Sparkles className="w-5 h-5 text-[#0E7490]" />
            AI Denial Risk Analysis
          </DialogTitle>
          <p className="text-sm text-[#64748B]">Pre-submission analysis of claim denial probability</p>
        </DialogHeader>

        {/* Claim summary bar */}
        {claim && (
          <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm" data-testid="prediction-claim-info">
            <span className="font-mono text-[#0E7490] font-medium">{claim.claim_number}</span>
            <span className="text-[#64748B]">{claim.patient_name}</span>
            <span className="text-[#64748B]">${claim.total_amount?.toLocaleString()}</span>
            {claim.insurance_provider && <span className="text-[#64748B]">{claim.insurance_provider}</span>}
          </div>
        )}

        <ScrollArea className="max-h-[60vh] pr-2">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="prediction-loading">
              <Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" />
              <p className="text-sm text-[#64748B]">Analyzing claim for denial risk...</p>
              <p className="text-xs text-[#94A3B8]">Checking code pairs, payer rules, and historical patterns</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="prediction-error">
              <FileWarning className="w-10 h-10 text-[#991B1B]" />
              <p className="text-sm text-[#991B1B]">{error}</p>
              <Button size="sm" onClick={runPrediction} className="mt-2">Retry</Button>
            </div>
          )}

          {prediction && !loading && (
            <div className="space-y-5" data-testid="prediction-results">
              {/* Risk score header */}
              <div className="flex items-center gap-4 p-4 rounded-xl border-2"
                style={{ borderColor: riskConfig.color, backgroundColor: `${riskConfig.color}08` }}
                data-testid="prediction-risk-score"
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${riskConfig.color}15` }}>
                  <span className="text-2xl font-bold" style={{ color: riskConfig.color }}>
                    {prediction.risk_score}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <RiskIcon className="w-5 h-5" style={{ color: riskConfig.color }} />
                    <span className="font-semibold text-lg" style={{ color: riskConfig.color }}>
                      {riskConfig.label}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ backgroundColor: `${riskConfig.color}15`, color: riskConfig.color }}>
                      {prediction.estimated_denial_probability} denial probability
                    </span>
                  </div>
                  <p className="text-sm text-[#64748B] mt-1">{prediction.summary}</p>
                </div>
              </div>

              {/* Risk gauge bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-[#94A3B8]">
                  <span>Low Risk</span>
                  <span>Critical Risk</span>
                </div>
                <div className="h-2 rounded-full bg-[#E2E8F0] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000"
                    style={{
                      width: `${prediction.risk_score}%`,
                      background: prediction.risk_score <= 25 ? '#166534' :
                        prediction.risk_score <= 50 ? '#92400E' :
                        prediction.risk_score <= 75 ? '#DC2626' : '#7F1D1D'
                    }}
                  />
                </div>
              </div>

              {/* Flags */}
              {prediction.flags && prediction.flags.length > 0 && (
                <div className="space-y-2" data-testid="prediction-flags">
                  <h3 className="text-sm font-semibold text-[#0A2342] flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#92400E]" />
                    Flagged Issues ({prediction.flags.length})
                  </h3>
                  {prediction.flags.map((flag, i) => (
                    <div key={i} className="p-3 rounded-lg border border-[#E2E8F0] bg-white space-y-2" data-testid={`prediction-flag-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${SEVERITY_BADGE[flag.severity] || SEVERITY_BADGE.medium}`}>
                          {flag.severity}
                        </span>
                        <span className="font-medium text-sm text-[#0A2342]">{flag.issue}</span>
                      </div>
                      <p className="text-sm text-[#64748B]">{flag.detail}</p>
                      <div className="flex items-start gap-2 p-2 rounded bg-[#0E7490]/5 text-sm">
                        <Lightbulb className="w-4 h-4 text-[#0E7490] flex-shrink-0 mt-0.5" />
                        <span className="text-[#0E7490]">{flag.suggestion}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Coding Recommendations */}
              {prediction.coding_recommendations && prediction.coding_recommendations.length > 0 && (
                <div className="space-y-2" data-testid="prediction-coding-recs">
                  <h3 className="text-sm font-semibold text-[#0A2342] flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0E7490]" />
                    Coding Recommendations
                  </h3>
                  <div className="overflow-hidden rounded-lg border border-[#E2E8F0]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                          <th className="text-left p-2 font-medium text-[#64748B]">Code</th>
                          <th className="text-left p-2 font-medium text-[#64748B]">Issue</th>
                          <th className="text-left p-2 font-medium text-[#64748B]">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prediction.coding_recommendations.map((rec, i) => (
                          <tr key={i} className="border-b border-[#E2E8F0] last:border-0">
                            <td className="p-2 font-mono text-[#0E7490]">{rec.current_code}</td>
                            <td className="p-2 text-[#64748B]">{rec.issue}</td>
                            <td className="p-2 text-[#0A2342]">{rec.recommended_action}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payer notes */}
              {prediction.payer_specific_notes && (
                <div className="p-3 rounded-lg bg-[#1A56DB]/5 border border-[#1A56DB]/20" data-testid="prediction-payer-notes">
                  <h3 className="text-sm font-semibold text-[#1A56DB] flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4" />
                    Payer-Specific Notes
                  </h3>
                  <p className="text-sm text-[#64748B]">{prediction.payer_specific_notes}</p>
                </div>
              )}

              {/* Disclaimer */}
              <p className="text-xs text-[#94A3B8] italic text-center pt-2 border-t border-[#E2E8F0]">
                AI-generated prediction for decision support. Final billing decisions require certified coder review.
              </p>
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end gap-3 pt-2 border-t border-[#E2E8F0]">
          {prediction && !loading && (
            <Button size="sm" variant="outline" onClick={runPrediction} data-testid="rerun-prediction-btn">
              Re-analyze
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onClose} data-testid="close-prediction-btn">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
